import { Registry, type Identified } from './Registry';
import { weightedPick, randInt } from '../core/Rng';

/**
 * Loot tabloları.
 * Sandık içeriği bulunduğu bölgeye göre değişir ve aynı içerik tekrarlanmaz
 * (her sandık kendi deterministik seed'iyle çekilir).
 */

export interface LootEntry {
  item: string;
  min: number;
  max: number;
  weight: number;
}

export interface LootTableDef extends Identified {
  name?: string;
  /** Kaç çekim yapılacak. */
  rollsMin: number;
  rollsMax: number;
  entries: LootEntry[];
  /** Kogox miktarı aralığı. */
  kogoxMin?: number;
  kogoxMax?: number;
}

export const lootTables = new Registry<LootTableDef>('loot');

lootTables.register(
  {
    id: 'chest_village',
    name: 'Köy Sandığı',
    rollsMin: 2,
    rollsMax: 4,
    kogoxMin: 5,
    kogoxMax: 40,
    entries: [
      { item: 'wood', min: 4, max: 12, weight: 100 },
      { item: 'stone', min: 4, max: 10, weight: 90 },
      { item: 'bread', min: 1, max: 3, weight: 60 },
      { item: 'fiber', min: 2, max: 6, weight: 60 },
      { item: 'wheat_seed', min: 1, max: 4, weight: 40 },
      { item: 'iron', min: 1, max: 3, weight: 25 },
      { item: 'rope', min: 1, max: 2, weight: 30 },
    ],
  },
  {
    id: 'chest_blacksmith',
    name: 'Demirci Sandığı',
    rollsMin: 2,
    rollsMax: 4,
    kogoxMin: 10,
    kogoxMax: 70,
    entries: [
      { item: 'iron', min: 2, max: 6, weight: 100 },
      { item: 'coal', min: 3, max: 8, weight: 90 },
      { item: 'copper', min: 2, max: 5, weight: 70 },
      { item: 'iron_sword', min: 1, max: 1, weight: 25 },
      { item: 'iron_pickaxe', min: 1, max: 1, weight: 25 },
      { item: 'silver', min: 1, max: 3, weight: 18 },
      { item: 'redstone', min: 1, max: 4, weight: 15 },
    ],
  },
  {
    id: 'chest_dungeon',
    name: 'Zindan Sandığı',
    rollsMin: 3,
    rollsMax: 5,
    kogoxMin: 30,
    kogoxMax: 160,
    entries: [
      { item: 'iron', min: 3, max: 8, weight: 80 },
      { item: 'gold', min: 1, max: 4, weight: 45 },
      { item: 'redstone', min: 2, max: 6, weight: 45 },
      { item: 'emerald', min: 1, max: 2, weight: 20 },
      { item: 'diamond', min: 1, max: 2, weight: 12 },
      { item: 'kogite', min: 1, max: 3, weight: 18 },
      { item: 'potion_strength', min: 1, max: 1, weight: 22 },
      { item: 'potion_speed', min: 1, max: 1, weight: 22 },
      { item: 'arrow', min: 8, max: 24, weight: 40 },
    ],
  },
  {
    id: 'chest_dungeon_deep',
    name: 'Derin Zindan Sandığı',
    rollsMin: 3,
    rollsMax: 6,
    kogoxMin: 80,
    kogoxMax: 400,
    entries: [
      { item: 'diamond', min: 1, max: 4, weight: 45 },
      { item: 'emerald', min: 1, max: 4, weight: 45 },
      { item: 'obsidian', min: 1, max: 3, weight: 35 },
      { item: 'voidsteel', min: 1, max: 2, weight: 14 },
      { item: 'blackDiamond', min: 1, max: 1, weight: 5 },
      { item: 'netherite', min: 1, max: 1, weight: 3 },
      { item: 'diamond_chest', min: 1, max: 1, weight: 8 },
      { item: 'potion_antivenom', min: 1, max: 2, weight: 20 },
    ],
  },
  {
    id: 'chest_tower_boss',
    name: 'Kule Ödülü',
    rollsMin: 4,
    rollsMax: 6,
    kogoxMin: 150,
    kogoxMax: 600,
    entries: [
      { item: 'diamond', min: 2, max: 5, weight: 50 },
      { item: 'kogite', min: 2, max: 6, weight: 45 },
      { item: 'voidsteel', min: 1, max: 3, weight: 22 },
      { item: 'blackDiamond', min: 1, max: 2, weight: 10 },
      { item: 'power_head', min: 1, max: 1, weight: 4 },
      { item: 'potion_strength', min: 2, max: 3, weight: 25 },
    ],
  },
  {
    id: 'chest_red_castle',
    name: 'Kızıl Kale Hazinesi',
    rollsMin: 5,
    rollsMax: 8,
    kogoxMin: 400,
    kogoxMax: 1200,
    entries: [
      { item: 'diamond', min: 3, max: 8, weight: 50 },
      { item: 'netherite', min: 1, max: 2, weight: 12 },
      { item: 'blackDiamond', min: 1, max: 3, weight: 14 },
      { item: 'voidsteel', min: 2, max: 4, weight: 25 },
      { item: 'power_chest', min: 1, max: 1, weight: 6 },
      { item: 'power_legs', min: 1, max: 1, weight: 6 },
      { item: 'emerald', min: 3, max: 8, weight: 40 },
    ],
  },
  {
    id: 'venom_node',
    name: 'Zehirli Bölge Kaynağı',
    rollsMin: 1,
    rollsMax: 3,
    entries: [
      { item: 'venom_sac', min: 1, max: 3, weight: 70 },
      { item: 'glow_mushroom', min: 1, max: 4, weight: 90 },
      { item: 'venomcrystal', min: 1, max: 2, weight: 20 },
    ],
  },
  {
    id: 'animal_small',
    rollsMin: 1,
    rollsMax: 2,
    entries: [
      { item: 'raw_meat', min: 1, max: 2, weight: 100 },
      { item: 'leather', min: 1, max: 1, weight: 40 },
      { item: 'feather', min: 1, max: 3, weight: 35 },
    ],
  },
  {
    id: 'animal_large',
    rollsMin: 2,
    rollsMax: 3,
    entries: [
      { item: 'raw_meat', min: 2, max: 5, weight: 100 },
      { item: 'leather', min: 1, max: 3, weight: 70 },
    ],
  },
);

export interface LootResult {
  items: Array<{ item: string; count: number }>;
  kogox: number;
}

/** Tabloyu deterministik bir RNG ile çeker (aynı sandık her açılışta aynı içerik). */
export function rollLoot(tableId: string, rand: () => number, bonus = 1): LootResult {
  const table = lootTables.get(tableId);
  if (!table) {
    console.warn(`[loot] bilinmeyen tablo: ${tableId}`);
    return { items: [], kogox: 0 };
  }
  const rolls = Math.max(1, Math.round(randInt(rand, table.rollsMin, table.rollsMax) * bonus));
  const acc = new Map<string, number>();
  const pool = table.entries.map((e) => ({ weight: e.weight, value: e }));
  for (let i = 0; i < rolls; i++) {
    const entry = weightedPick(rand, pool);
    if (!entry) continue;
    const count = Math.max(1, Math.round(randInt(rand, entry.min, entry.max) * bonus));
    acc.set(entry.item, (acc.get(entry.item) ?? 0) + count);
  }
  const kogox =
    table.kogoxMin !== undefined && table.kogoxMax !== undefined
      ? Math.round(randInt(rand, table.kogoxMin, table.kogoxMax) * bonus)
      : 0;
  return { items: [...acc].map(([item, count]) => ({ item, count })), kogox };
}
